import { FreightTypeInput, FreightTypeProps } from '@/shared/inputs/FreightTypeInput.tsx';
import { DisposalPurposeInput, DisposalPurposeProps } from '@/shared/inputs/DisposalPurposeInput.tsx';
import { QuantityAndUnitInput, QuantityAndUnitInputProps } from '@/shared/inputs/QuantityAndUnitInput.tsx';
import { SelectOrganizationInput, SelectOrganizationInputProps } from '@/shared/inputs/SelectOrganizationInput.tsx';
import { PriceAndTaxClassInput, PriceAndTaxClassProps } from '@/shared/inputs/PriceAndTaxClassInput.tsx';
import { PRODUCT_CATEGORY, PRODUCT_TYPES } from '@schuettflix/interfaces';
import { DatePickerFieldInput } from '@/shared/inputs/DatePickerFieldInput.tsx';
import { UseControllerProps, useFormContext } from 'react-hook-form';
import { PRODUCT_CATEGORY_LABEL } from '@/phrase/ProductCategoryLabels.ts';
import { DeleteIcon } from '@schuettflix/icons-react';
import { useTranslation } from 'react-i18next';
import { isWasteFreightType } from '@/shared/domain/product/isWasteFreightType.ts';
import { isTransportProduct } from '@/shared/domain/product/isTransportProduct.ts';
import { cn } from '@/shared/utils/cn.ts';
import {
    CustomProduct,
    CustomProductsFormValues,
} from '@/shared/forms/CustomProductsForm/schemas/getCustomProductsFormSchema.ts';
import { TextFieldInput } from '@/shared/inputs/TextFieldInput.tsx';
import { ProductConfigurationScope } from '@/shared/types/ProductConfigurationScope.ts';
import { getProductCategoryIcon } from '@/CustomRequestProductChannel/utils/getProductCategoryIcon.ts';
import { PRODUCT_PLACEHOLDER_LABEL } from '@/phrase/ProductPlaceHolderLabel.ts';
import { PurchasePriceHints } from '../../../components/PurchasePriceHints.tsx';
import { SalesPriceHints } from '../../../components/SalesPriceHints.tsx';
import { getLimitedDateRanges } from '@/shared/utils/getLimitedDateRanges.ts';
import { useOrganizationSuspenseQuery } from '@/clients/organization/useOrganization.ts';

type CustomProductIndexPath<TField extends string> = `customProducts.${number}.${TField}`;

type NewCustomRequestProductProps = {
    customerOrganizationId: number;
    scope: ProductConfigurationScope;
    productType: PRODUCT_TYPES;
    productCategory: PRODUCT_CATEGORY;
    productNameProps: Pick<
        UseControllerProps<CustomProductsFormValues, CustomProductIndexPath<'productName'>>,
        'name' | 'defaultValue' | 'disabled'
    >;
    freightTypeProps: Pick<
        UseControllerProps<CustomProductsFormValues, CustomProductIndexPath<'freightType'>>,
        'name' | 'defaultValue' | 'disabled'
    > &
        Omit<FreightTypeProps, 'name' | 'defaultValue' | 'disabled'>;
    currencyCodeProps: Pick<
        UseControllerProps<CustomProductsFormValues, CustomProductIndexPath<'currencyCode'>>,
        'name'
    >;
    disposalPurposeProps: Pick<
        UseControllerProps<CustomProductsFormValues, CustomProductIndexPath<'disposalPurpose'>>,
        'name' | 'defaultValue'
    > &
        Omit<DisposalPurposeProps, 'name' | 'defaultValue'>;
    quantityProps: Pick<
        UseControllerProps<CustomProductsFormValues, CustomProductIndexPath<'quantity'>>,
        'name' | 'defaultValue' | 'disabled'
    > &
        Omit<QuantityAndUnitInputProps['quantityProps'], 'name' | 'defaultValue' | 'disabled'>;
    unitProps: Pick<
        UseControllerProps<CustomProductsFormValues, CustomProductIndexPath<'unit'>>,
        'name' | 'defaultValue'
    > &
        Omit<QuantityAndUnitInputProps['unitProps'], 'name' | 'defaultValue'>;
    partnerSelectionProps: Pick<
        UseControllerProps<CustomProductsFormValues, CustomProductIndexPath<'partnerOrganizationId'>>,
        'name' | 'defaultValue'
    > &
        Pick<SelectOrganizationInputProps, 'defaultValueLabel' | 'exclude' | 'disabled'>;
    purchasePriceProps: Pick<
        UseControllerProps<CustomProductsFormValues, CustomProductIndexPath<'purchasePrice'>>,
        'name' | 'defaultValue'
    > &
        Omit<PriceAndTaxClassProps['priceProps'], 'name' | 'defaultValue'>;
    purchaseTaxClassIdProps: Pick<
        UseControllerProps<CustomProductsFormValues, CustomProductIndexPath<'purchaseTaxClassId'>>,
        'name' | 'defaultValue'
    > &
        Omit<PriceAndTaxClassProps['taxClassProps'], 'name' | 'defaultValue'>;
    salesPriceProps: Pick<
        UseControllerProps<CustomProductsFormValues, CustomProductIndexPath<'salesPrice'>>,
        'name' | 'defaultValue'
    > &
        Omit<PriceAndTaxClassProps['priceProps'], 'name' | 'defaultValue'>;
    salesTaxClassIdProps: Pick<
        UseControllerProps<CustomProductsFormValues, CustomProductIndexPath<'salesTaxClassId'>>,
        'name' | 'defaultValue'
    > &
        Omit<PriceAndTaxClassProps['taxClassProps'], 'name' | 'defaultValue'>;
    serviceDateProps: Pick<
        UseControllerProps<CustomProductsFormValues, CustomProductIndexPath<'serviceDate'>>,
        'name' | 'defaultValue' | 'disabled'
    >;
    showZeroPurchasePriceProps: Pick<
        UseControllerProps<CustomProductsFormValues, CustomProductIndexPath<'showZeroPurchasePrice'>>,
        'name' | 'defaultValue' | 'disabled'
    >;
    showZeroSalesPriceProps: Pick<
        UseControllerProps<CustomProductsFormValues, CustomProductIndexPath<'showZeroSalesPrice'>>,
        'name' | 'defaultValue' | 'disabled'
    >;
    showFrancoHint: boolean;
    showNonFrancoHint: boolean;
    disableDelete?: boolean;
    onDeleteCustomProduct?: () => void;
    onUpdateField?: <T extends keyof CustomProduct, F extends CustomProduct[T]>(fieldName: T, value: F) => void;
};

export const NewCustomRequestProduct = ({
    customerOrganizationId,
    scope,
    productType,
    productCategory,
    productNameProps,
    freightTypeProps,
    currencyCodeProps,
    disposalPurposeProps,
    quantityProps,
    unitProps,
    partnerSelectionProps,
    purchasePriceProps,
    purchaseTaxClassIdProps,
    salesPriceProps,
    salesTaxClassIdProps,
    serviceDateProps,
    showZeroPurchasePriceProps,
    showZeroSalesPriceProps,
    showFrancoHint,
    showNonFrancoHint,
    disableDelete = false,
    onDeleteCustomProduct,
    onUpdateField,
}: NewCustomRequestProductProps) => {
    const { t } = useTranslation();
    const { watch, setValue } = useFormContext<CustomProductsFormValues>();
    const { data: defaultPartnerOrganization } = useOrganizationSuspenseQuery(
        typeof partnerSelectionProps.defaultValue === 'number' ? partnerSelectionProps.defaultValue : undefined
    );
    const LeadingIcon = getProductCategoryIcon(productCategory);

    const freightType = watch(freightTypeProps.name);
    const unit = watch(unitProps.name);
    const currencyCode = watch(currencyCodeProps.name);
    const purchasePrice = watch(purchasePriceProps.name);
    const partnerOrganizationId = watch(partnerSelectionProps.name);
    const salesPrice = watch(salesPriceProps.name);
    const showZeroPurchasePrice = watch(showZeroPurchasePriceProps.name);
    const showZeroSalesPrice = watch(showZeroSalesPriceProps.name);

    const isVisibleFreightType = isTransportProduct(productType);
    const isVisibleDisposalPurpose = isWasteFreightType(freightType);
    const isVisibleServiceDate = scope === 'ORDER';

    const { minDate, maxDate } = getLimitedDateRanges();

    return (
        <div
            className="[counter-increment:productCounter] first:pt-0"
            data-test={`custom-request-product-configuration-form-${productType}-${productCategory}`}
        >
            <div className="col-span-3 flex justify-between pb-4">
                <p className="font-copy-sm-strong uppercase before:content-[counter(productCounter)'.\0020']">
                    {t(PRODUCT_CATEGORY_LABEL[productCategory])}
                </p>
                <div className="flex items-center justify-center">
                    <DeleteIcon
                        monochrome
                        className={cn('hover:cursor-pointer', {
                            'hover:cursor-not-allowed': disableDelete,
                        })}
                        disabled={disableDelete}
                        onClick={() => (disableDelete ? null : onDeleteCustomProduct?.())}
                    />
                </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
                <TextFieldInput
                    {...productNameProps}
                    leadingSlot={<LeadingIcon />}
                    placeHolder={t(PRODUCT_PLACEHOLDER_LABEL[productType])}
                    size="lg"
                    className={cn('col-span-full', {
                        'col-span-2': isVisibleFreightType,
                        'col-span-1': isVisibleDisposalPurpose,
                    })}
                    onTextFieldChange={productName => {
                        if (typeof productName === 'string') {
                            onUpdateField?.('productName', productName);
                        }
                    }}
                />
                {isVisibleFreightType && <FreightTypeInput {...freightTypeProps} dataTest="freight-type-input" />}
                {isVisibleDisposalPurpose && (
                    <DisposalPurposeInput
                        {...disposalPurposeProps}
                        placeholder={t('forms.inputs.disposalProcedureInput.label')}
                    />
                )}

                <QuantityAndUnitInput
                    quantityProps={{
                        ...quantityProps,
                        onQuantityChange: quantity => {
                            if (typeof quantity === 'number') {
                                onUpdateField?.('quantity', quantity);
                            }
                        },
                    }}
                    unitProps={{
                        ...unitProps,
                        onUnitChange: unit => {
                            if (unit) {
                                onUpdateField?.('unit', unit);
                            }
                        },
                    }}
                    className="col-start-1"
                />
                <SelectOrganizationInput
                    {...partnerSelectionProps}
                    defaultValue={partnerSelectionProps.defaultValue}
                    defaultValueLabel={defaultPartnerOrganization?.name}
                    placeholder={t('product.addPosition.partner.label')}
                    dataTest="partner-organization"
                    exclude={{ ids: [customerOrganizationId] }}
                    className="col-span-2"
                    onValueChange={org => {
                        if (org) {
                            onUpdateField?.('partnerOrganizationId', org.id);
                        }
                    }}
                />
                <PriceAndTaxClassInput
                    priceProps={{
                        ...purchasePriceProps,
                        unit,
                        currencyCode,
                        testId: 'purchase-price',
                        onPriceChange: price => {
                            if (typeof price === 'number') {
                                onUpdateField?.('purchasePrice', price);
                            }
                        },
                    }}
                    taxClassProps={{
                        ...purchaseTaxClassIdProps,
                        testId: 'purchase-tax-class',
                        onTaxClassChange: taxClass => {
                            if (taxClass) {
                                onUpdateField?.('purchaseTaxClassId', taxClass);
                            }
                        },
                    }}
                />
                <PriceAndTaxClassInput
                    priceProps={{
                        ...salesPriceProps,
                        unit,
                        testId: 'sales-price',
                        currencyCode,
                        onPriceChange: price => {
                            if (typeof price === 'number') {
                                onUpdateField?.('salesPrice', price);
                            }
                        },
                    }}
                    taxClassProps={{
                        ...salesTaxClassIdProps,
                        testId: 'sales-tax-class',
                        onTaxClassChange: taxClass => {
                            if (taxClass) {
                                onUpdateField?.('salesTaxClassId', taxClass);
                            }
                        },
                    }}
                />
                {isVisibleServiceDate && (
                    <DatePickerFieldInput
                        {...serviceDateProps}
                        dataTest="service-date-picker"
                        placeHolder={t('product.quote.serviceDate')}
                        minDate={minDate.toISOString()}
                        maxDate={maxDate.toISOString()}
                        mode="single"
                        onDatePickerFieldChange={serviceDate => {
                            if (serviceDate) {
                                onUpdateField?.('serviceDate', serviceDate);
                            }
                        }}
                    />
                )}
            </div>
            <div className="mt-4 flex flex-col gap-4">
                {!showZeroPurchasePriceProps.disabled && (
                    <PurchasePriceHints
                        partnerOrganizationId={partnerOrganizationId}
                        purchasePrice={purchasePrice}
                        showZeroPurchasePrice={showZeroPurchasePrice}
                        onZeroPurchasePriceChange={() =>
                            setValue(showZeroPurchasePriceProps.name, !showZeroPurchasePrice)
                        }
                    />
                )}

                {!showZeroSalesPriceProps.disabled && (
                    <SalesPriceHints
                        customerOrganizationId={customerOrganizationId}
                        salesPrice={salesPrice}
                        showZeroSalesPrice={showZeroSalesPrice}
                        onZeroSalesPriceChange={() => setValue(showZeroSalesPriceProps.name, !showZeroSalesPrice)}
                    />
                )}

                {showFrancoHint && (
                    <p className="font-copy-sm text-subdued" data-test="franco-hint">
                        {t('sections.productSelection.productHint.productPriceAdded')}
                    </p>
                )}

                {showNonFrancoHint && (
                    <p className="font-copy-sm text-subdued" data-test="non-franco-hint">
                        {t('sections.productSelection.productHint.productPriceNotAdded')}
                    </p>
                )}
            </div>
        </div>
    );
};
